import React, { useContext, useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { LayoutContext } from "../../context/LayoutContext";
import BaseWrapper from "../../components/ui/BaseWrapper";
import DateRangeShortcutPicker from "../../components/ui/DateRangeShortcutPicker";

import EngineerKpiCards from "./EngineerKpiCards";
import EngineerPerformanceRing from "./EngineerPerformanceRing";
import EngineerInvoiceStats from "./EngineerInvoiceStats";

import useAuth from "../../hooks/useAuth";

import {
  fetchEngineerKpi,
  fetchEngineerInvoiceStats,
} from "../../services/api/engineerAnalytics";

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const now = new Date();
const defaultStart = toDateStr(new Date(now.getFullYear(), 0, 1));
const defaultEnd = toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 0));

// ─── Scroll-fade animation wrapper ───────────────────────────────────────────
const Fade = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.06 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ─── Section header ────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, subtitle, accent = "#0F40AE" }) => (
  <div className="flex items-center gap-3 mb-4">
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: `${accent}18` }}
    >
      <i className={`${icon} text-sm`} style={{ color: accent }} />
    </div>
    <div>
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      {subtitle && <p className="text-[11px] text-gray-400">{subtitle}</p>}
    </div>
  </div>
);

// ─── Pulse dot for "live" data ────────────────────────────────────────────
const LiveBadge = () => (
  <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
    Live
  </span>
);

const EngineerDashboard = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const { auth } = useAuth();
  const user = auth?.user;
  // Earnings are visible to freelance company owners and individual freelancers,
  // but hidden from freelance company members.
  const canViewEarnings = !user?.isCompanyMember;
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const filterParams = {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };

  const { data: kpiResult } = useQuery({
    queryKey: ["eng-kpi", startDate, endDate],
    queryFn: () => fetchEngineerKpi(filterParams),
    staleTime: 60_000,
  });

  const { data: invoiceStatsResult } = useQuery({
    queryKey: ["eng-invoiceStats", startDate, endDate],
    queryFn: () => fetchEngineerInvoiceStats(filterParams),
    staleTime: 60_000,
    enabled: canViewEarnings,
  });

  const kpi = kpiResult?.data;
  const invoiceStats = invoiceStatsResult?.data;

  return (
    <div
      className={`bg-[#F6F7FB] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full overflow-y-auto transition-all ${
        isBannerVisible ? "top-[106px] h-[calc(100vh-106px)]" : "top-[56px] h-[calc(100vh-56px)]"
      }`}
    >
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="border-b border-zinc-200 bg-white">
        <BaseWrapper>
          <div className="flex items-center justify-between py-[6.5px] gap-3 flex-wrap">
            <div className="flex justify-between">
              <h3 className="font-light">Analytics</h3>
            </div>

            <div className="flex items-center gap-2">
              <DateRangeShortcutPicker
                startDate={startDate}
                endDate={endDate}
                onChange={(s, e) => {
                  setStartDate(s);
                  setEndDate(e);
                }}
              />
            </div>
          </div>
        </BaseWrapper>
      </div>

      <BaseWrapper className="py-5 flex flex-col gap-5">
        {/* ── KPI Cards ─────────────────────────────────────────────────── */}
        <Fade delay={0}>
          <EngineerKpiCards data={kpi?.kpis} />
        </Fade>

        {/* ── Invoice Stats (Earnings) — hidden from freelance company members ─ */}
        {canViewEarnings && (
          <Fade delay={20}>
            <EngineerInvoiceStats data={invoiceStats} />
          </Fade>
        )}

        {/* ── Performance rings ─────────────────────────────────────────── */}
        <Fade delay={60}>
          <EngineerPerformanceRing summary={kpi?.summary} />
        </Fade>

        <div className="h-4" />
      </BaseWrapper>
    </div>
  );
};

export default EngineerDashboard;
